import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useApiProvider } from '../providers/index.ts';

const AllowedExternalRedirectOrigins = [
  'https://femcng.com',
  'https://www.femcng.com'
];

const buildUrlOrNull = (url: string) => {
  try {
    return new URL(url);
  } catch (e) {
    return null;
  }
};

const useSignInRedirect = Object.assign(
  () => {
    const navigate = useNavigate();
    const location = useLocation();

    const params = new URLSearchParams(location.search);
    params.set('next', location.pathname);

    return () => {
      navigate(`/auth/signin?${params}`);
    };
  },
  {
    useConsumeRedirect: () => {
      const navigate = useNavigate();
      const [params] = useSearchParams();

      return (defaultPath: string) => {
        const next = params.get('next');
        if (typeof next !== 'string') {
          navigate(defaultPath);
          return;
        }
        navigate(next);
      };
    },
    useConsumeExternalRedirect: () => {
      const { apiAuthenticated } = useApiProvider();
      const [params] = useSearchParams();

      const external = params.get('external');
      const next = params.get('next');
      let externalUrl = external !== null ? buildUrlOrNull(next ?? '') : null;

      if (
        externalUrl &&
        !AllowedExternalRedirectOrigins.includes(externalUrl.origin)
      ) {
        console.warn(
          `External redirect origin ${externalUrl.origin} is not allowed`
        );
        externalUrl = null;
      }

      return [
        externalUrl,
        async () => {
          if (!externalUrl) {
            return;
          }

          const response = await apiAuthenticated.post<{ data: string }>(
            '/users/authorize-external'
          );

          externalUrl.searchParams.set('token', response.data.data);
          window.location.href = externalUrl.toString();
        }
      ] as const;
    },
    buildRedirectUrl: (originalUrl: string) => {
      const params = new URLSearchParams();
      params.set('next', originalUrl);
      return `/auth/signin?${params}`;
    }
  }
);

export { useSignInRedirect };
